import React, { createContext, ReactNode, useContext, useEffect } from 'react';
import * as GoogleTagManager from '../google-tag-manager';
import * as Mixpanel from '../mixpanel';
import * as Sentry from '../sentry';
import {
  AnalyticsConfig,
  AnalyticsEvent,
  PageViewEvent,
  UserIdentity
} from '../types';

import * as Hubspot from '../hubspot';

interface AnalyticsContextType {
  identify: (user: UserIdentity) => void;
  track: (event: AnalyticsEvent) => void;
  trackPageView: (event: PageViewEvent) => void;
  captureException: (error: Error, context?: Record<string, any>) => void;
  captureMessage: (
    message: string,
    level?: 'info' | 'warning' | 'error'
  ) => void;
  setContext: (key: string, context: Record<string, any>) => void;
  reset: () => void;
}

const AnalyticsContext = createContext<AnalyticsContextType>({
  identify: () => {},
  track: () => {},
  trackPageView: () => {},
  captureException: () => {},
  captureMessage: () => {},
  setContext: () => {},
  reset: () => {}
});

interface AnalyticsProviderProps {
  children: ReactNode;
  config: AnalyticsConfig;
  user?: UserIdentity;
  backendBaseUrl?: string;
}

export const AnalyticsProvider: React.FC<AnalyticsProviderProps> = ({
  children,
  config,
  user,
  backendBaseUrl
}) => {
  useEffect(() => {
    // Initialize all analytics services
    if (config.sentry) {
      // Add backend base URL to Sentry config if provided
      const sentryConfig = {
        ...config,
        sentry: {
          ...config.sentry,
          ...(backendBaseUrl && {
            tracePropagationTargets: [
              ...(config.sentry.tracePropagationTargets || []),
              backendBaseUrl
            ],
            failedRequestTargets: [
              backendBaseUrl,
              /^https:\/\/.*\.mygrowdash\.com$/
            ]
          })
        }
      };
      Sentry.initSentry(sentryConfig, user);
    }

    GoogleTagManager.initGoogleTagManager(config, user);
    Mixpanel.initMixpanel(config, user);
    Hubspot.initHubSpot(config, user);
  }, [config, user, backendBaseUrl]);

  const identify = (userToIdentify: UserIdentity): void => {
    Sentry.identifyUser(userToIdentify);
    GoogleTagManager.identifyUser(userToIdentify);
    Mixpanel.identifyUser(userToIdentify);
    Hubspot.identifyUser(userToIdentify);
    Mixpanel.setUser(userToIdentify);
  };

  const track = (event: AnalyticsEvent): void => {
    Mixpanel.trackEvent(event);
    GoogleTagManager.trackEvent(event);
    Hubspot.trackEvent(event);
  };

  const trackPageView = (event: PageViewEvent): void => {
    GoogleTagManager.trackPageView(event);
    Mixpanel.trackPageView(event);
    Hubspot.trackPageView(event);
  };

  const captureException = (
    error: Error,
    context?: Record<string, any>
  ): void => {
    Sentry.captureException(error, context);
  };

  const captureMessage = (
    message: string,
    level: 'info' | 'warning' | 'error' = 'info'
  ): void => {
    Sentry.captureMessage(message, level);
  };

  const setContext = (key: string, context: Record<string, any>): void => {
    Sentry.setContext(key, context);
  };

  const reset = (): void => {
    // Reset other services by calling their reset functions
    GoogleTagManager.resetGoogleTagManager();
    Mixpanel.resetMixpanel();
    Sentry.clearContext();
    Hubspot.resetHubSpot();
  };

  const contextValue: AnalyticsContextType = {
    identify,
    track,
    trackPageView,
    captureException,
    captureMessage,
    setContext,
    reset
  };

  return (
    <AnalyticsContext.Provider value={contextValue}>
      <Mixpanel.MixpanelProvider config={config} user={user}>
        {children}
      </Mixpanel.MixpanelProvider>
    </AnalyticsContext.Provider>
  );
};

export const useAnalytics = (): AnalyticsContextType => {
  return useContext(AnalyticsContext);
};

export type { AnalyticsContextType };
